/**
 * @author chrisl / Geodan
 *
 * adapted from Potree.FirstPersonControls by
 *
 * @author mschuetz / http://mschuetz.at
 *
 * and THREE.DeviceOrientationControls  by
 *
 * @author richt / http://richt.me
 * @author WestLangley / http://github.com/WestLangley
 *
 *
 *
 */

import * as THREE from 'three';
import { EventDispatcher } from '../EventDispatcher';

/** Loose interface for the Potree viewer used by DeviceOrientationControls */
interface PotreeViewer {
  renderer: THREE.WebGLRenderer;
  scene: {
    cameraP: THREE.PerspectiveCamera;
  };
}

/** Subset of DeviceOrientationEvent properties we read */
interface DeviceOrientationData {
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
}

export class DeviceOrientationControls extends EventDispatcher {
  viewer: PotreeViewer;
  renderer: THREE.WebGLRenderer;
  enabled: boolean;
  scene: { getActiveCamera(): THREE.Camera } | null;
  sceneControls: THREE.Scene;
  screenOrientation: number;
  deviceOrientation: DeviceOrientationData | undefined;

  constructor(viewer: PotreeViewer) {
    super();

    this.viewer = viewer;
    this.renderer = viewer.renderer;
    this.enabled = true;

    this.scene = null;
    this.sceneControls = new THREE.Scene();

    this.screenOrientation = (window.orientation as number) || 0;

    const deviceOrientationChange = (e: DeviceOrientationEvent) => {
      this.deviceOrientation = e;
    };

    const screenOrientationChange = (_e: Event) => {
      this.screenOrientation = (window.orientation as number) || 0;
    };

    if ('ondeviceorientationabsolute' in window) {
      (window as Window).addEventListener('deviceorientationabsolute', deviceOrientationChange);
    } else if ('ondeviceorientation' in window) {
      (window as Window).addEventListener('deviceorientation', deviceOrientationChange);
    } else {
      console.warn('No device orientation found.');
    }
    // window.addEventListener('deviceorientation', deviceOrientationChange);
    window.addEventListener('orientationchange', screenOrientationChange);
  }

  stop() {
    // noop
  }

  setScene(scene: { getActiveCamera(): THREE.Camera }) {
    this.scene = scene;
  }

  update(_delta: number) {
    const computeQuaternion = function (alpha: number, beta: number, gamma: number, orient: number) {
      const quaternion = new THREE.Quaternion();

      const zee = new THREE.Vector3(0, 0, 1);
      const euler = new THREE.Euler();
      const q0 = new THREE.Quaternion();

      euler.set(beta, gamma, alpha, 'ZXY');
      quaternion.setFromEuler(euler);
      quaternion.multiply(q0.setFromAxisAngle(zee, -orient));

      return quaternion;
    };

    if (typeof this.deviceOrientation !== 'undefined') {
      const alpha = this.deviceOrientation.alpha ? THREE.MathUtils.degToRad(this.deviceOrientation.alpha) : 0;
      const beta = this.deviceOrientation.beta ? THREE.MathUtils.degToRad(this.deviceOrientation.beta) : 0;
      const gamma = this.deviceOrientation.gamma ? THREE.MathUtils.degToRad(this.deviceOrientation.gamma) : 0;
      const orient = this.screenOrientation ? THREE.MathUtils.degToRad(this.screenOrientation) : 0;

      const quaternion = computeQuaternion(alpha, beta, gamma, orient);
      this.viewer.scene.cameraP.quaternion.set(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
    }
  }
}
